import React, { useRef } from 'react';
import { Navigation, Scrollbar } from 'swiper';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  breakpointSizes,
  brandColours,
  standardTransition,
} from '../styles';
import { Container, Heading, Slider } from './ui';
import ImageContentCard from './ImageContentCard';
import arrowIcon from '../images/right-arrow.svg';

const StyledImageContentCards = styled.section`
  ${sectionPaddings()};
  background-color: ${standardColours.lighterGrey};
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 30px;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 50px;
  `}
`;

const StyledInner = styled.div`
  position: relative;

  .swiper {
    position: static;
  }

  .swiper-slide {
    height: auto;
  }

  .swiper-scrollbar {
    position: static;
    margin: 30px auto 0;
    height: 5px;
    max-width: 312px;

    ${minBreakpointQuery.small`
      margin-top: 40px;
    `}

    ${minBreakpointQuery.medium`
      margin-top: 50px;
    `}

    ${minBreakpointQuery.large`
      margin-top: 60px;
    `}

    ${minBreakpointQuery.xxlarge`
      margin-top: 70px;
    `}

    ${minBreakpointQuery.xxxxlarge`
      margin-top: 80px;
    `}

    .swiper-scrollbar-drag {
      background-color: ${brandColours.primary};
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    &:after {
      content: none;
    }

    ${minBreakpointQuery.xxlarge`
      height: 75px;
      width: 75px;
      background-color: ${brandColours.primary};
      border-radius: 50%;
      transform: translateY(-50%);
      transition: ${standardTransition('background-color')};
      cursor: pointer;
    `}

    &:before {
      ${minBreakpointQuery.xxlarge`
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 22px;
        height: 22px;
        background: url(${arrowIcon}) no-repeat center center;
        background-size: contain;
        filter: brightness(0) invert(1);
      `}
    }

    &:hover {
      ${minBreakpointQuery.xxlarge`
        background-color: ${brandColours.secondary};
      `}
    }
  }

  .swiper-button-prev {
    ${minBreakpointQuery.xxlarge`
      left: -100px;
    `}

    &:before {
      ${minBreakpointQuery.xxlarge`
        transform: translate(-50%, -50%) rotate(180deg);
      `}
    }
  }

  .swiper-button-next {
    ${minBreakpointQuery.xxlarge`
      right: -100px;
    `}
  }
`;

const ImageContentCards = ({ heading, items }) => {
  const sliderRef = useRef();

  const sliderOptions = {
    slidesPerView: 1,
    spaceBetween: 10,
    breakpoints: {
      [breakpointSizes.small]: {
        slidesPerView: 2,
        spaceBetween: 15,
      },
      [breakpointSizes.medium]: {
        slidesPerView: 3,
        spaceBetween: 20,
      },
    },
    modules: [Scrollbar, Navigation],
    navigation: {
      enabled: true,
      hideOnClick: false,
    },
    loop: true,
    scrollbar: { draggable: true },
    onBeforeInit: swiper => {
      sliderRef.current = swiper;
    },
  };

  return (
    <StyledImageContentCards>
      <Container>
        <StyledHeading>{heading}</StyledHeading>
        <StyledInner>
          <Slider {...sliderOptions}>
            {items.map((item, id) => (
              <ImageContentCard key={id} {...item} />
            ))}
          </Slider>
        </StyledInner>
      </Container>
    </StyledImageContentCards>
  );
};

export default ImageContentCards;
